import {
  format,
  formatDistanceStrict,
  formatDuration,
  intervalToDuration,
} from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

export const getTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

export class DateUtils {
  /**
   * Ensures consistent formatting for time components
   */
  static serializeDate(dateString: string): string {
    if (!dateString || !dateString.includes('T')) {
      return dateString;
    }

    const [datePart, timePart] = dateString.split('T');

    if (!timePart) {
      return dateString;
    }

    // Split time components and pad with zeros if needed
    const timeComponents = timePart.split(':');
    const hour = (timeComponents[0] || '0').padStart(2, '0');
    const minute = (timeComponents[1] || '0').padStart(2, '0');
    const second = (timeComponents[2] || '0').padStart(2, '0');

    // Reconstruct the date with properly formatted time
    return `${datePart}T${hour}:${minute}:${second}`;
  }

  /**
   * Explicitly converts date to UTC using date-fns-tz
   */
  static convertDateToUTC(dateString: string): string {
    if (!dateString) {
      return dateString;
    }

    try {
      // Parse the date string and convert to UTC
      const userTimezone = getTimeZone();

      // First, parse as if it's in user's timezone
      const localDate = new Date(dateString);

      // Check if date is valid before proceeding
      if (isNaN(localDate.getTime())) {
        return dateString; // Return original if invalid
      }

      // Convert to UTC using date-fns-tz
      const utcDate = zonedTimeToUtc(localDate, userTimezone);

      // Format back to the expected string format
      return utcDate.toISOString();
    } catch (error) {
      return dateString;
    }
  }

  static getDate(date: Date): string {
    return date.toLocaleDateString();
  }

  static getTime(date: Date): string {
    return date.toLocaleTimeString();
  }

  static getDateTime(date: Date): string {
    return `${DateUtils.getDate(date)} ${DateUtils.getTime(date)}`;
  }

  static getFormattedDiff(
    start?: string,
    end?: any,
    range?: 'full' | 'd-h-m-s'
  ): string {
    if (!start || !end) {
      return 'NA';
    }

    // Use the enhanced normalization for consistent timezone handling
    const normalizedStart = DateUtils.normalizeToUTC(start);
    const normalizedEnd = DateUtils.normalizeToUTC(end);

    if (!normalizedStart || !normalizedEnd) {
      return 'NA';
    }

    // Ensure start is before end, if not swap them
    let startDate = normalizedStart;
    let endDate = normalizedEnd;

    if (startDate.getTime() > endDate.getTime()) {
      [startDate, endDate] = [endDate, startDate];
    }

    // even positions have plural form
    const formatRanges = {
      years: 'y',
      year: 'y',
      months: 'm',
      month: 'm',
      weeks: 'w',
      week: 'w',
      days: 'd',
      day: 'd',
      hours: 'h',
      hour: 'h',
      minutes: 'm',
      minute: 'm',
      seconds: 's',
      second: 's',
    };

    const onlyChars = /[a-z]+/gi;
    let duration;

    try {
      if (range === 'd-h-m-s') {
        const durationObject = intervalToDuration({
          start: startDate,
          end: endDate,
        });

        // Calculate exact hours for more accurate day calculation
        const totalMilliseconds = endDate.getTime() - startDate.getTime();
        const totalHours = Math.floor(totalMilliseconds / (1000 * 60 * 60));

        if (durationObject.days !== undefined || totalHours >= 24) {
          durationObject.days =
            Math.floor(totalHours / 24) + (durationObject.years ?? 0) * 365;
          durationObject.hours = totalHours % 24;
        }

        duration = formatDuration(durationObject, {
          format: ['days', 'hours', 'minutes', 'seconds'],
        });
      } else {
        duration = formatDuration(
          intervalToDuration({
            start: startDate,
            end: endDate,
          }),
          {
            format: Object.keys(formatRanges).filter((x, i) => i % 2 === 0),
          }
        );
      }

      const formattedDuration = DateUtils.replaceAll(
        duration.trim().replace(/\s/g, ''),
        formatRanges
      );
      return formattedDuration.replace(onlyChars, (matched) => `${matched} `);
    } catch (error) {
      return 'NA';
    }
  }

  /**
   * Enhanced normalization for various date formats with explicit UTC conversion
   */
  private static parseZulu(dateString: string): Date | null {
    if (dateString.includes('T') && dateString.endsWith('Z')) {
      const parsed = new Date(dateString);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }

  private static parseWithOffset(dateString: string): Date | null {
    if (dateString.includes('T') && /(T.*[-+])/.test(dateString)) {
      const parsed = new Date(dateString);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }

  private static parseLegacyFormat(dateString: string): Date | null {
    if (!dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}, \d{2}:\d{2}:\d{2}$/)) {
      return null;
    }

    const [datePart, timePart] = dateString.split(', ');
    const [month, day, year] = datePart.split('/').map(Number);
    const [hours, minutes, seconds] = timePart.split(':').map(Number);

    return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
  }

  private static parseISOWithoutTimezone(dateString: string): Date | null {
    if (
      !dateString.includes('T') ||
      dateString.includes('Z') ||
      /(T.*[-+])/.test(dateString)
    ) {
      return null;
    }

    const serialized = DateUtils.serializeDate(dateString);
    const withZ = `${serialized}Z`;
    const parsed = new Date(withZ);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private static parseWithFallback(dateString: string): Date | null {
    const parsed = new Date(dateString);
    if (isNaN(parsed.getTime())) {
      return null;
    }

    if (
      !dateString.includes('Z') &&
      !/(T.*[-+])/.test(dateString.substring(dateString.indexOf('T')))
    ) {
      const utcMilliseconds =
        parsed.getTime() + parsed.getTimezoneOffset() * 60000;
      return new Date(utcMilliseconds);
    }

    return parsed;
  }

  static normalizeToUTC(dateString: string): Date | null {
    if (!dateString) {
      return null;
    }
    try {
      return (
        DateUtils.parseZulu(dateString) ||
        DateUtils.parseWithOffset(dateString) ||
        DateUtils.parseLegacyFormat(dateString) ||
        DateUtils.parseISOWithoutTimezone(dateString) ||
        DateUtils.parseWithFallback(dateString)
      );
    } catch (error) {
      return null;
    }
  }

  /* Formatting methods */

  static replaceAll(str: string, mapObj: Record<string, string>): string {
    const re = new RegExp(Object.keys(mapObj).join('|'), 'gi');

    return str.replace(re, (matched) => mapObj[matched.toLowerCase()]);
  }

  static formatUtcToLocalTimezone(utcDate: string): string {
    if (!utcDate) {
      return '';
    }
    try {
      const date = DateUtils.normalizeToUTC(utcDate);
      if (!date || isNaN(date.getTime())) {
        return utcDate;
      }

      const userTimezone = getTimeZone();
      const zonedDate = utcToZonedTime(date, userTimezone);

      return format(zonedDate, 'M/d/yyyy, HH:mm:ss');
    } catch (e) {
      return utcDate;
    }
  }

  static formatDateFromUtcString(
    utcDate: string,
    applyTimeZone = false
  ): string {
    if (utcDate === null || utcDate === undefined || utcDate === '') {
      return '';
    }

    const timezone = applyTimeZone ? getTimeZone() : 'UTC';
    const date = utcToZonedTime(new Date(utcDate), timezone);

    return date.toLocaleString(navigator.language, { hour12: false });
  }

  static formatDateForDetail(utcDate: string): string {
    if (utcDate === null || utcDate === undefined || utcDate === '') {
      return '';
    }
    const date = utcToZonedTime(new Date(utcDate), 'UTC');

    return format(date, 'dd MMMM yyyy HH:mm');
  }
}
